import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import pluralize from 'pluralize'
import { CalendarIcon, UsersIcon, AwardIcon, PackageIcon, PencilIcon, Trophy } from 'lucide-react'
import { Button } from '../../ui/button'
import Avatar from '../Avatar/Avatar'
import Badge from '../Badge/Badge'
import { levelArrayToString } from '../../../utilities/helpers'
import { getSportLabel } from '../../../utilities/sportOptions'
import ClassTimesDrawer from './ClassTimesDrawer'
import CoachingSettingsDrawer from './CoachingSettingsDrawer'
import ClassPackagesDrawer from './ClassPackagesDrawer'
import StudentsDrawer from './StudentsDrawer'
import CurriculumDrawer from './CurriculumDrawer'

function KlassForm({
  onSubmit,
  dates = [],
  coaches = [],
  students: selectedStudents = [],
  supportedSkillLevels = [],
  klassPackages: selectedKlassPackages = [],
  maxStudents,
  initialSport = 'skating',
  initialCurriculumId = null,
  initialCurriculumMode = 'existing'
}) {
  const { facility_id: facilityId } = useParams()
  const [activeDrawer, setActiveDrawer] = useState(null)
  const [curriculumSkillLevels, setCurriculumSkillLevels] = useState(supportedSkillLevels)
  const [curriculumName, setCurriculumName] = useState(null)

  useEffect(() => {
    if (supportedSkillLevels.length > 0) {
      setCurriculumSkillLevels(supportedSkillLevels)
    }
  }, [supportedSkillLevels])

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting }
  } = useForm({
    defaultValues: {
      dates,
      coaches: coaches.filter((x) => x.selected),
      supportedSkillLevels: supportedSkillLevels.filter((x) => x.selected),
      klassPackages: selectedKlassPackages,
      students: selectedStudents,
      maxStudents,
      sport: initialSport,
      curriculumId: initialCurriculumId,
      curriculumMode: initialCurriculumMode,
      selectedSkillLevelIds: supportedSkillLevels.filter((x) => x.selected).map((x) => x.id),
      customSkillLevels: []
    }
  })

  const formValues = watch()

  function getSpotsInfo() {
    const enrolledCount = formValues.students.filter((s) => s.selected).length

    if (!formValues.maxStudents) {
      return { text: 'Unlimited spots', color: 'green' }
    }

    const spotsLeft = formValues.maxStudents - enrolledCount

    if (spotsLeft >= 4) {
      return { text: `${spotsLeft} ${pluralize('spot', spotsLeft)} available`, color: 'green' }
    } else if (spotsLeft > 0) {
      return { text: `${spotsLeft} ${pluralize('spot', spotsLeft)} available`, color: 'orange' }
    } else {
      return { text: 'Full', color: 'red' }
    }
  }

  const spotsInfo = getSpotsInfo()

  function handleSportChange(sport) {
    setValue('sport', sport)
    setValue('curriculumId', null)
    setValue('supportedSkillLevels', [])
    setValue('selectedSkillLevelIds', [])
    setCurriculumSkillLevels([])
    setCurriculumName(null)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="min-h-screen bg-gray-100 pb-10">
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">New Class</h1>
          <p className="mt-2 text-sm text-gray-600">Set up your class schedule, coaching settings, and enrollment</p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Trophy className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Sport & Curriculum</h2>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setActiveDrawer('curriculum')}>
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>

            <div className="px-6 py-4 space-y-3">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Sport</label>
                <p className="text-sm text-gray-900">{getSportLabel(formValues.sport)}</p>
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Curriculum</label>
                <p className="text-sm text-gray-900">
                  {formValues.curriculumMode === 'custom'
                    ? `Custom (${formValues.customSkillLevels?.length || 0} levels)`
                    : formValues.curriculumId
                      ? curriculumName || 'Selected curriculum'
                      : 'Not selected'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Class Schedule</h2>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setActiveDrawer('times')}>
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>

            <div className="px-6 py-4">
              {formValues.dates.filter((d) => d.isTimeSet).length === 0 ? (
                <p className="text-sm text-gray-500">No class times set</p>
              ) : (
                <div className="space-y-2">
                  {formValues.dates
                    .filter((d) => d.isTimeSet)
                    .map((dateItem, idx) => (
                      <div key={idx} className="px-3 py-2 text-sm bg-gray-50 rounded-md">
                        {dateItem.date?.format ? dateItem.date.format('dddd, MMM D') : dateItem.dayOfWeek} - {dateItem.date?.format ? dateItem.date.format('h:mm A') : dateItem.timeOfDay}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <UsersIcon className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Coaching Settings</h2>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setActiveDrawer('coaching')}>
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Coaches</label>
                {formValues.coaches.length === 0 ? (
                  <p className="text-sm text-gray-500">No coaches assigned</p>
                ) : (
                  <div className="flex overflow-hidden">
                    {formValues.coaches.map((coach, index) => (
                      <Avatar
                        key={coach.id}
                        fullName={coach.fullName}
                        cloudinaryPhotoPublicId={coach.cloudinaryPhotoPublicId}
                        alt={`Photo of ${coach.fullName}`}
                        userId={coach.id}
                        size={10}
                        className={`${index !== 0 ? '-ml-3' : ''} inline-block border-2 border-white rounded-full`}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Skill Levels</label>
                <p className="text-sm text-gray-900">
                  {formValues.supportedSkillLevels.length === 0
                    ? 'Any level'
                    : levelArrayToString(
                        formValues.supportedSkillLevels.map((s) => s.label),
                        { maxLength: 50 }
                      )}
                </p>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Class Size</label>
                <p className="text-sm text-gray-900">
                  {formValues.maxStudents ? `${formValues.maxStudents} students max` : 'Unlimited'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <AwardIcon className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Enrollment</h2>
              </div>
              <Badge text={spotsInfo.text} color={spotsInfo.color} />
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Current Students</span>
                <span className="text-2xl font-semibold text-gray-900">
                  {formValues.students.filter((s) => s.selected).length}
                </span>
              </div>

              <Button type="button" variant="outline" className="w-full" onClick={() => setActiveDrawer('students')}>
                <PencilIcon className="w-4 h-4 mr-2" />
                Manage Students
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <PackageIcon className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Class Packages</h2>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setActiveDrawer('packages')}>
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>

            <div className="px-6 py-4">
              {formValues.klassPackages.filter((kp) => kp.selected).length === 0 ? (
                <p className="text-sm text-gray-500">Not part of any packages</p>
              ) : (
                <div className="space-y-2">
                  {formValues.klassPackages
                    .filter((kp) => kp.selected)
                    .map((pkg) => (
                      <div key={pkg.id} className="px-3 py-2 text-sm bg-purple-50 rounded-md">
                        {pkg.label}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button type="submit" disabled={isSubmitting} variant="default" size="lg">
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <ClassTimesDrawer
        open={activeDrawer === 'times'}
        onOpenChange={(open) => !open && setActiveDrawer(null)}
        dates={formValues.dates}
        onChange={(newDates) => setValue('dates', newDates)}
      />

      <CoachingSettingsDrawer
        open={activeDrawer === 'coaching'}
        onOpenChange={(open) => !open && setActiveDrawer(null)}
        coaches={coaches}
        supportedSkillLevels={curriculumSkillLevels}
        selectedCoaches={formValues.coaches}
        selectedSkillLevels={formValues.supportedSkillLevels}
        maxStudents={formValues.maxStudents}
        onCoachesChange={(newCoaches) => setValue('coaches', newCoaches)}
        onSkillLevelsChange={(newLevels) => setValue('supportedSkillLevels', newLevels)}
        onMaxStudentsChange={(max) => setValue('maxStudents', max)}
        facilityId={facilityId}
      />

      <ClassPackagesDrawer
        open={activeDrawer === 'packages'}
        onOpenChange={(open) => !open && setActiveDrawer(null)}
        klassPackages={formValues.klassPackages}
        onChange={(newPackages) => setValue('klassPackages', newPackages)}
      />

      <StudentsDrawer
        open={activeDrawer === 'students'}
        onOpenChange={(open) => !open && setActiveDrawer(null)}
        students={formValues.students}
        selectedSkillLevels={formValues.supportedSkillLevels}
        onChange={(newStudents) => setValue('students', newStudents)}
        facilityId={facilityId}
      />

      <CurriculumDrawer
        open={activeDrawer === 'curriculum'}
        onOpenChange={(open) => !open && setActiveDrawer(null)}
        facilityId={facilityId}
        selectedSport={formValues.sport}
        selectedCurriculumId={formValues.curriculumId}
        selectedSkillLevelIds={formValues.selectedSkillLevelIds || []}
        customSkillLevels={formValues.customSkillLevels || []}
        curriculumMode={formValues.curriculumMode}
        onSportChange={handleSportChange}
        onCurriculumChange={(id) => setValue('curriculumId', id)}
        onSkillLevelIdsChange={(ids) => setValue('selectedSkillLevelIds', ids)}
        onCustomSkillLevelsChange={(levels) => setValue('customSkillLevels', levels)}
        onModeChange={(mode) => setValue('curriculumMode', mode)}
        onCurriculumSkillLevelsChange={(skillLevels, name, clearSelections = true) => {
          setCurriculumSkillLevels(skillLevels)
          if (name) {
            setCurriculumName(name)
          }
          if (clearSelections) {
            setValue('supportedSkillLevels', [])
          }
        }}
      />
    </form>
  )
}

KlassForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  dates: PropTypes.array,
  coaches: PropTypes.array,
  klassPackages: PropTypes.array,
  supportedSkillLevels: PropTypes.array,
  students: PropTypes.array,
  maxStudents: PropTypes.number,
  initialSport: PropTypes.string,
  initialCurriculumId: PropTypes.number,
  initialCurriculumMode: PropTypes.oneOf(['existing', 'custom'])
}

export default KlassForm
